/**
 * Скелетоны кабинета хозяйства (agroai-6al.49): по одному на вкладку, в
 * раскладке самой вкладки — те же рамки, настоящие заголовки карточек, та же
 * высота карты и графика. Шапка и ряд вкладок скелетона не требуют: они
 * видны сразу. Данные встают на место без сдвига раскладки.
 */
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { Grid, Stack } from '../../ui/Card';
import {
  Skeleton,
  SkeletonCard,
  SkeletonChart,
  SkeletonKpi,
  SkeletonList,
  SkeletonMap,
  SkeletonRegion,
  SkeletonTable,
  SkeletonText,
} from '../../ui/Skeleton';

const m = defineMessages({
  lMap: { id: 'farm.skeleton.lMap', defaultMessage: 'карту', description: 'Что грузится: карта; название загружаемого блока в винительном падеже, читается диктором как «Загружаю …»; строчными' },
  lFarmState: { id: 'farm.skeleton.lFarmState', defaultMessage: 'состояние хозяйства', description: 'Что грузится: обзор хозяйства; название загружаемого блока в винительном падеже, читается диктором как «Загружаю …»; строчными' },
  lCycle: { id: 'farm.skeleton.lCycle', defaultMessage: 'агроцикл', description: 'Что грузится: карточка агроцикла; название загружаемого блока в винительном падеже, читается диктором как «Загружаю …»; строчными' },
  lCycleWorks: { id: 'farm.skeleton.lCycleWorks', defaultMessage: 'работы агроцикла', description: 'Что грузится: таблица работ агроцикла; название загружаемого блока в винительном падеже, читается диктором как «Загружаю …»; строчными' },
  lFarmCycles: { id: 'farm.skeleton.lFarmCycles', defaultMessage: 'агроциклы хозяйства', description: 'Что грузится: вкладка «Агроциклы и работы»; название загружаемого блока в винительном падеже, читается диктором как «Загружаю …»; строчными' },
  lWater: { id: 'farm.skeleton.lWater', defaultMessage: 'воду по поливам', description: 'Что грузится: вкладка «Вода»; название загружаемого блока в винительном падеже, читается диктором как «Загружаю …»; строчными' },
  lQuarantine: { id: 'farm.skeleton.lQuarantine', defaultMessage: 'карантин хозяйства', description: 'Что грузится: вкладка «Карантин»; название загружаемого блока в винительном падеже, читается диктором как «Загружаю …»; строчными' },
  lFinds: { id: 'farm.skeleton.lFinds', defaultMessage: 'находки', description: 'Что грузится: таблица находок карантина; название загружаемого блока в винительном падеже, читается диктором как «Загружаю …»; строчными' },
  lHarvestPlan: { id: 'farm.skeleton.lHarvestPlan', defaultMessage: 'план и сдачу', description: 'Что грузится: блок плана и сдачи урожая; название загружаемого блока в винительном падеже, читается диктором как «Загружаю …»; строчными' },
  lHarvest: { id: 'farm.skeleton.lHarvest', defaultMessage: 'сдачу урожая', description: 'Что грузится: вкладка «Сдача»; название загружаемого блока в винительном падеже, читается диктором как «Загружаю …»; строчными' },
  lHarvestDays: { id: 'farm.skeleton.lHarvestDays', defaultMessage: 'сдачу по дням', description: 'Что грузится: график сдачи урожая по дням; название загружаемого блока в винительном падеже, читается диктором как «Загружаю …»; строчными' },
  lContours: { id: 'farm.skeleton.lContours', defaultMessage: 'контуры хозяйства', description: 'Что грузится: таблица контуров хозяйства; название загружаемого блока в винительном падеже, читается диктором как «Загружаю …»; строчными' },
  lWaterWeeks: { id: 'farm.skeleton.lWaterWeeks', defaultMessage: 'воду по неделям', description: 'Что грузится: график воды по неделям; название загружаемого блока в винительном падеже, читается диктором как «Загружаю …»; строчными' },
  lNowCycles: { id: 'farm.skeleton.lNowCycles', defaultMessage: 'идущие агроциклы', description: 'Что грузится: блок «Сейчас» с идущими агроциклами; название загружаемого блока в винительном падеже, читается диктором как «Загружаю …»; строчными' },
  tNow: { id: 'farm.skeleton.tNow', defaultMessage: 'Сейчас', description: 'Заголовок карточки-заглушки: блок «Сейчас» на вкладке «Обзор»' },
  tKpi: { id: 'farm.skeleton.tKpi', defaultMessage: 'Ключевые показатели', description: 'Заголовок карточки-заглушки: ключевые показатели хозяйства' },
  tLand: { id: 'farm.skeleton.tLand', defaultMessage: 'Земля хозяйства', description: 'Заголовок карточки-заглушки: земля хозяйства' },
  tDirections: { id: 'farm.skeleton.tDirections', defaultMessage: 'Направления', description: 'Заголовок карточки-заглушки: направления (культуры по сезонам)' },
  tMap: { id: 'farm.skeleton.tMap', defaultMessage: 'Карта хозяйства', description: 'Заголовок карточки-заглушки: карта хозяйства' },
  tContoursByStatus: { id: 'farm.skeleton.tContoursByStatus', defaultMessage: 'Контуры хозяйства по статусу работ', description: 'Заголовок карточки-заглушки: карта контуров по статусу работ агроцикла' },
  tCycleWorks: { id: 'farm.skeleton.tCycleWorks', defaultMessage: 'Работы агроцикла', description: 'Заголовок карточки-заглушки: таблица работ агроцикла' },
  tWaterings: { id: 'farm.skeleton.tWaterings', defaultMessage: 'Поливы агроцикла', description: 'Заголовок карточки-заглушки: список поливов агроцикла' },
  tWaterChart: { id: 'farm.skeleton.tWaterChart', defaultMessage: 'Запрошено и подано по поливам', description: 'Заголовок графика-заглушки: запрошенная и поданная вода по поливам' },
  tQMap: { id: 'farm.skeleton.tQMap', defaultMessage: 'Карта карантина хозяйства', description: 'Заголовок карточки-заглушки: карта карантина' },
  tQFoci: { id: 'farm.skeleton.tQFoci', defaultMessage: 'Очаги и предписания', description: 'Заголовок карточки-заглушки: очаги и предписания карантина' },
  tQMonths: { id: 'farm.skeleton.tQMonths', defaultMessage: 'Карантин по месяцам', description: 'Заголовок карточки-заглушки: динамика карантина по месяцам' },
  sRedYellow: { id: 'farm.skeleton.sRedYellow', defaultMessage: 'Контуры в жёлтом и красном', description: 'Подзаголовок графика-заглушки: число контуров в жёлтом и красном карантине по месяцам' },
  sInspections: { id: 'farm.skeleton.sInspections', defaultMessage: 'Обследования', description: 'Подзаголовок графика-заглушки: число карантинных обследований по месяцам' },
  tQFinds: { id: 'farm.skeleton.tQFinds', defaultMessage: 'Находки, предписания и сроки', description: 'Заголовок таблицы-заглушки: находки, предписания и их сроки' },
  tQChanges: { id: 'farm.skeleton.tQChanges', defaultMessage: 'Смена статусов', description: 'Заголовок карточки-заглушки: история смены карантинных статусов' },
  tHarvest: { id: 'farm.skeleton.tHarvest', defaultMessage: 'Сдача урожая', description: 'Заголовок карточки-заглушки: сдача урожая' },
  sWaterWeeks: { id: 'farm.skeleton.sWaterWeeks', defaultMessage: 'Запрошено и подано по неделям', description: 'Подзаголовок графика-заглушки: запрошенная и поданная вода по неделям' },
});

/** Карта и легенда-фильтр рядом: сетка `cab-mapgrid`. */
export function MapWithLegendSkeleton({ height, legendRows = 4, below = false, label }: { height: number; legendRows?: number; below?: boolean; label?: string }) {
  const t = useT();
  return (
    <div className={`cab-mapgrid${below ? ' cab-mapgrid--below' : ''}`}>
      <SkeletonMap quiet label={label ?? t(m.lMap)} height={height} />
      <SkeletonList quiet items={legendRows} tone="soft" />
    </div>
  );
}

/** Поле графика внутри карточки: столбики на базовой линии, высота — графика. */
export function PlotSkeleton({ height, bars = 12 }: { height: number; bars?: number }) {
  return (
    <div className="skel-chart__plot" style={{ height }} aria-hidden="true">
      {Array.from({ length: bars }, (_, i) => (
        <span key={i} className="skel skel--block skel-chart__bar" style={{ height: `${30 + ((i * 37) % 55)}%` }} />
      ))}
    </div>
  );
}

/** Строка «Показано: …» над данными. */
function FilterLine() {
  return <Skeleton width={360} className="skel--caption" />;
}

/** Ряд из n KPI-карточек в сетке экрана. */
function KpiRow({ n }: { n: 3 | 4 }) {
  return (
    <Grid cols={n}>
      {Array.from({ length: n }, (_, i) => <SkeletonKpi key={i} quiet />)}
    </Grid>
  );
}

/** «Обзор»: Сейчас, Ключевые показатели, Земля, Направления, Карта. */
export function OverviewSkeleton({ nowCount = 1 }: { nowCount?: number }) {
  const t = useT();
  return (
    <SkeletonRegion label={t(m.lFarmState)}>
      <Stack>
        <SkeletonCard quiet size="lg" title={t(m.tNow)}>
          <NowCyclesSkeleton quiet count={nowCount} />
        </SkeletonCard>
        <SkeletonCard quiet title={t(m.tKpi)}>
          <div className="cab-kpis">
            {Array.from({ length: 4 }, (_, i) => <SkeletonKpi key={i} quiet />)}
          </div>
        </SkeletonCard>
        <SkeletonCard quiet title={t(m.tLand)}>
          <KpiRow n={4} />
        </SkeletonCard>
        <SkeletonCard quiet title={t(m.tDirections)}>
          <SkeletonList quiet items={2} tone="soft" />
        </SkeletonCard>
        <SkeletonCard quiet size="lg" title={t(m.tMap)}>
          <SkeletonMap quiet height={440} />
        </SkeletonCard>
      </Stack>
    </SkeletonRegion>
  );
}

/** Ряд карточек агроциклов (`cab-cycles`). */
function CycleCardsSkeleton() {
  const t = useT();
  return (
    <div className="cab-cycles">
      {Array.from({ length: 3 }, (_, i) => (
        <SkeletonRegion key={i} quiet label={t(m.lCycle)} className="cab-cycle">
          <Skeleton width="60%" className="skel--title" />
          <Skeleton width="80%" className="skel--caption" />
          <Skeleton variant="block" width="100%" height={8} />
          <Skeleton width="90%" className="skel--caption" />
          <Skeleton width="70%" className="skel--caption" />
        </SkeletonRegion>
      ))}
    </div>
  );
}

/** Карточка работ цикла: таблица. */
export function CycleWorksBodySkeleton() {
  const t = useT();
  return <SkeletonTable quiet label={t(m.lCycleWorks)} rows={6} cols={7} />;
}

/** «Агроциклы и работы» до ответа по циклам. */
export function CyclesSkeleton() {
  const t = useT();
  return (
    <SkeletonRegion label={t(m.lFarmCycles)}>
      <Stack>
        <FilterLine />
        <CycleCardsSkeleton />
        <SkeletonCard quiet size="lg" title={t(m.tContoursByStatus)}>
          <MapWithLegendSkeleton height={440} legendRows={5} />
        </SkeletonCard>
        <SkeletonCard quiet size="lg" title={t(m.tCycleWorks)}>
          <CycleWorksBodySkeleton />
        </SkeletonCard>
      </Stack>
    </SkeletonRegion>
  );
}

/** Тело «Поливы цикла»: лента слева, выбранный полив справа. */
function WateringsBodySkeleton() {
  return (
    <div className="wv-split">
      <div className="wv-split__rail">
        <SkeletonList quiet items={4} />
      </div>
      <div className="wv-split__main">
        <Stack>
          <Skeleton width="45%" className="skel--title" />
          <KpiRow n={4} />
          <MapWithLegendSkeleton height={420} legendRows={4} below />
        </Stack>
      </div>
    </div>
  );
}

/** Выбор цикла и «Показано» над данными воды. */
function WaterFilterRowSkeleton() {
  return (
    <div className="cab-filterrow">
      <div className="cab-chips" aria-hidden="true">
        {Array.from({ length: 2 }, (_, i) => <Skeleton key={i} variant="block" width={120} height={32} className="skel--control" />)}
      </div>
      <FilterLine />
    </div>
  );
}

/** «Вода» до ответа по поливам: KPI, поливы, график. `withFilter` — ещё и ряд выбора цикла. */
export function WateringSkeleton({ withFilter = false }: { withFilter?: boolean }) {
  const t = useT();
  return (
    <SkeletonRegion label={t(m.lWater)}>
      <Stack>
        {withFilter ? <WaterFilterRowSkeleton /> : null}
        <KpiRow n={4} />
        <SkeletonCard quiet size="lg" title={t(m.tWaterings)}>
          <WateringsBodySkeleton />
        </SkeletonCard>
        <SkeletonChart quiet title={t(m.tWaterChart)} height={220} bars={8} captionLines={1} />
      </Stack>
    </SkeletonRegion>
  );
}

/** «Карантин» до ответа: KPI, карта | очаги, месяцы, находки, события. */
export function QuarantineSkeleton() {
  const t = useT();
  return (
    <SkeletonRegion label={t(m.lQuarantine)}>
      <Stack>
        <FilterLine />
        <KpiRow n={4} />
        <div className="cab-qsplit">
          <SkeletonCard quiet size="lg" title={t(m.tQMap)}>
            <MapWithLegendSkeleton height={460} legendRows={3} />
          </SkeletonCard>
          <SkeletonCard quiet size="lg" title={t(m.tQFoci)}>
            <SkeletonList quiet items={4} tone="soft" />
          </SkeletonCard>
        </div>
        <SkeletonCard quiet title={t(m.tQMonths)}>
          <div className="cab-split cab-split--even">
            <div>
              <strong className="cab-subhead">{t(m.sRedYellow)}</strong>
              <PlotSkeleton height={180} bars={13} />
            </div>
            <div>
              <strong className="cab-subhead">{t(m.sInspections)}</strong>
              <PlotSkeleton height={180} bars={13} />
            </div>
          </div>
        </SkeletonCard>
        <SkeletonTable quiet title={t(m.tQFinds)} label={t(m.lFinds)} rows={5} cols={4} />
        <SkeletonCard quiet title={t(m.tQChanges)}>
          <SkeletonList quiet items={4} tone="soft" />
        </SkeletonCard>
      </Stack>
    </SkeletonRegion>
  );
}

/** Тело карточки «Сдача урожая» до плана и партий: KPI и график. */
export function HarvestBodySkeleton() {
  const t = useT();
  return (
    <SkeletonRegion label={t(m.lHarvestPlan)}>
      <Stack tight>
        <KpiRow n={4} />
        <PlotSkeleton height={180} bars={14} />
      </Stack>
    </SkeletonRegion>
  );
}

/** «Сдача» до ответа по циклам: карточка целиком. */
export function HarvestSkeleton() {
  const t = useT();
  return (
    <SkeletonRegion label={t(m.lHarvest)}>
      <SkeletonCard quiet size="lg" title={t(m.tHarvest)}>
        <div className="cab-chips" aria-hidden="true">
          {Array.from({ length: 2 }, (_, i) => <Skeleton key={i} variant="block" width={120} height={28} className="skel--control" />)}
        </div>
        <Stack tight>
          <KpiRow n={4} />
          <SkeletonChart quiet label={t(m.lHarvestDays)} height={180} bars={14} />
        </Stack>
      </SkeletonCard>
    </SkeletonRegion>
  );
}

/** Таблица контуров хозяйства до ответа по земле. */
export function ContoursTableSkeleton() {
  const t = useT();
  return <SkeletonTable label={t(m.lContours)} rows={10} cols={7} />;
}

/** Журнал воды в раскрытии: график по неделям. */
export function WaterWeeksSkeleton() {
  const t = useT();
  return (
    <SkeletonRegion label={t(m.lWaterWeeks)}>
      <strong className="cab-subhead">{t(m.sWaterWeeks)}</strong>
      <PlotSkeleton height={180} bars={14} />
    </SkeletonRegion>
  );
}

/** «Сейчас»: карточки идущих циклов в разметке `cab-now` — слева доли, справа работы. */
export function NowCyclesSkeleton({ count = 1, quiet }: { count?: number; quiet?: boolean }) {
  const t = useT();
  return (
    <SkeletonRegion label={t(m.lNowCycles)} quiet={quiet} className="cab-now">
      {Array.from({ length: count }, (_, k) => (
        <div key={k} className="cab-now__cycle" aria-hidden="true">
          <div className="cab-now__head">
            <Skeleton variant="block" width="55%" height={24} />
            <Skeleton width="80%" className="skel--caption" />
            <Skeleton width="50%" className="skel--caption" />
            {Array.from({ length: 3 }, (_, i) => <Skeleton key={i} variant="block" width="100%" height={22} />)}
            <SkeletonText lines={3} />
          </div>
          <div>
            <Skeleton width={130} className="skel--title" />
            <SkeletonList quiet items={3} tone="soft" />
          </div>
        </div>
      ))}
    </SkeletonRegion>
  );
}
